import { jsx, jsxs } from 'react/jsx-runtime';
import React__default, { useEffect, useMemo, useCallback } from 'react';
import { formatPercent } from '@fabric-app/utils/formatting';
import { styled } from 'styled-components';
import { hasMacOSKbd } from '@fabric-app/utils/detection';
import { Tooltip } from '../Tooltip.js';
import { cssVar } from '../../theme/variables.js';
import { mediaHover } from '../../constants/styled-utils.js';

const defaultZoomSequence = [
  0.5,
  0.67,
  0.75,
  0.8,
  0.9,
  1,
  1.1,
  1.2,
  1.3,
  1.4,
  1.5,
  1.6,
  1.7,
  1.8,
  1.9,
  2
];
const ZoomSlider = ({ steps = defaultZoomSequence, value, onChange, style, className, hideButtonControls = false, showZoomLevel = false, formatter = formatPercent, orientation = "horizontal", tooltipDisabled = false }) => {
  const currentStep = Math.max(0, Math.min(steps.indexOf(value), steps.length - 1));
  const currentPosition = Math.max(0, Math.min(currentStep * 100 / (steps.length - 1), 100));
  const [hasMacOSKbdState, setHasMacOSKbdState] = React__default.useState(false);
  useEffect(() => {
    setHasMacOSKbdState(hasMacOSKbd());
  }, []);
  const minValue = useMemo(() => Math.min(...steps), [
    steps
  ]);
  const maxValue = useMemo(() => Math.max(...steps), [
    steps
  ]);
  const [trackRef, setTrackRef] = React__default.useState(null);
  const getValue = useCallback((e) => {
    if (!trackRef) return;
    const { left, width, top, height } = trackRef.getBoundingClientRect();
    const distance = orientation === "horizontal" ? e.clientX - left : e.clientY - top;
    const st = orientation === "horizontal" ? distance / width : distance / height;
    const step = Math.max(0, Math.min(Math.round(st * (steps.length - 1)), steps.length - 1));
    return steps[step];
  }, [
    orientation,
    steps,
    trackRef
  ]);
  const onMouseDown = React__default.useCallback((e) => {
    if (!trackRef) return;
    e.preventDefault();
    e.stopPropagation();
    const value2 = getValue(e);
    if (!value2 || value2 < minValue || value2 > maxValue) return;
    onChange(value2);
    const onMouseMove = (e2) => {
      const value3 = getValue(e2);
      if (!value3 || value3 < minValue || value3 > maxValue) return;
      onChange(value3);
    };
    const onMouseUp = () => {
      document.removeEventListener("pointermove", onMouseMove);
      document.removeEventListener("pointerup", onMouseUp);
    };
    document.addEventListener("pointermove", onMouseMove);
    document.addEventListener("pointerup", onMouseUp);
  }, [
    maxValue,
    minValue,
    onChange,
    trackRef,
    getValue
  ]);
  const zoomUp = React__default.useCallback(() => {
    const index = steps.indexOf(value);
    if (index === steps.length - 1) return;
    onChange(steps[index + 1]);
  }, [
    onChange,
    steps,
    value
  ]);
  const zoomDown = React__default.useCallback(() => {
    const index = steps.indexOf(value);
    if (index === 0) return;
    onChange(steps[index - 1]);
  }, [
    onChange,
    steps,
    value
  ]);
  return /* @__PURE__ */ jsx(Tooltip, {
    disabled: tooltipDisabled,
    label: `Zoom (${hasMacOSKbdState ? "⌘" : "Ctrl"}+Scroll)`,
    placement: "bottom",
    children: /* @__PURE__ */ jsxs(ZoomSliderContainer, {
      "data-zoom-slider-container": true,
      className,
      style,
      children: [
        /* @__PURE__ */ jsx("input", {
          type: "hidden",
          value
        }),
        !hideButtonControls && /* @__PURE__ */ jsx(ZoomSliderButton, {
          onClick: zoomDown,
          children: /* @__PURE__ */ jsx("span", {
            style: {
              paddingTop: 1
            },
            children: "-"
          })
        }),
        /* @__PURE__ */ jsx(ZoomSliderTrackContainer, {
          ref: setTrackRef,
          onPointerDown: onMouseDown,
          children: /* @__PURE__ */ jsx(ZoomSliderTrack, {
            children: /* @__PURE__ */ jsx(ZoomSliderThumb, {
              style: {
                left: `${currentPosition}%`
              }
            })
          })
        }),
        !hideButtonControls && /* @__PURE__ */ jsx(ZoomSliderButton, {
          onClick: zoomUp,
          children: /* @__PURE__ */ jsx("span", {
            children: "+"
          })
        }),
        showZoomLevel && /* @__PURE__ */ jsx(ZoomSliderValue, {
          children: formatter(value)
        })
      ]
    })
  });
};
const ZoomSliderValue = styled.div.withConfig({
  displayName: "ZoomSlider__ZoomSliderValue",
  componentId: "sc-28df1f90-0"
})([
  `display:flex;justify-content:flex-end;padding-left:0.5rem;width:2.5rem;color:`,
  `;`
], cssVar["color-text-secondary"]);
const ZoomSliderButton = styled.button.withConfig({
  displayName: "ZoomSlider__ZoomSliderButton",
  componentId: "sc-28df1f90-1"
})([
  `position:relative;opacity:0;width:14px;height:14px;display:flex;align-items:flex-end;justify-content:center;font-weight:500;font-size:18px;line-height:16px;color:`,
  `;span{position:absolute;bottom:50%;height:9.5px;}`
], cssVar["color-text-secondary"]);
const ZoomSliderTrack = styled.div.withConfig({
  displayName: "ZoomSlider__ZoomSliderTrack",
  componentId: "sc-28df1f90-2"
})([
  `position:relative;width:99px;height:4px;background:`,
  `;border-radius:2px;cursor:pointer;flex-grow:1;`
], cssVar["color-zoom-slider-track"]);
const ZoomSliderThumb = styled.div.withConfig({
  displayName: "ZoomSlider__ZoomSliderThumb",
  componentId: "sc-28df1f90-3"
})([
  `position:absolute;top:0;width:12px;height:12px;background:`,
  `;cursor:pointer;border-radius:50%;transform:translate(-50%,-4px);transition:left 0.15s ease-in-out;`
], cssVar["color-text-tertiary"]);
const ZoomSliderContainer = styled.div.withConfig({
  displayName: "ZoomSlider__ZoomSliderContainer",
  componentId: "sc-28df1f90-4"
})([
  `display:flex;align-items:center;gap:6px;height:30px;`,
  `{&:hover{`,
  `{opacity:1;}}}`
], mediaHover, ZoomSliderButton);
const ZoomSliderTrackContainer = styled.div.withConfig({
  displayName: "ZoomSlider__ZoomSliderTrackContainer",
  componentId: "sc-28df1f90-5"
})([
  `height:100%;flex-grow:1;display:flex;align-items:center;`
]);

export { ZoomSlider as default, defaultZoomSequence };
//# sourceMappingURL=ZoomSlider.js.map
